/**
 * CodeConfigurationsTypes.ts
 *
 * Shared TypeScript types and enums for the Code Execution Configuration system.
 * Describes the shapes for entry points, execution configs, and exit points that
 * make up a `CodeExecutionConfig` — the persisted model that wires a user-defined
 * function to the dashboard events and side effects that drive it.
 *
 * See also:
 * - @./CodeConfigurator.tsx - Top-level UI that lists and edits these configurations.
 * - @./CodeConfigurationsCard.tsx - Renders a single CodeExecutionConfig as a card.
 * - @./CodeConfigurationsEditor.tsx - Editor dialog for creating/editing one config.
 */

import type { ScaleDataState } from "@/components/ec/endlaTypes";
import type { ColumnDataType, ModelDataTypes } from "@/endlaFunctions/customTypings/databaseNew";
import type { ModelName, SchemaColumnType } from "@/endlaFunctions/customTypings/ecTypes";
import type { TemplateBaseModel } from "@/endlaFunctions/customTypings/models";

export enum EntryPointType {
  Initialization = "initialization",
  Plot = "plot",
  // Receives an EnrichedSubgraph (selected paths walked into a tree).
  PipelineGraph = "pipeline graph",
  Schedule = "schedule",
  LogPlot = "log plot",
  Button = "button",
  DataTable = "data table",
  Data = "data",
  Event = "event",
  DataScope = "data scope",
}

export enum EventEntryPoint {
  DataSync = "dataSync",
}

export enum ExitPointType {
  Data = "data",
  DataField = "data field",
  Notify = "notify",
  Fittings = "fittings",
  DataTable = "data table",
  DataRow = "data row",
  NoAction = "no action",
}

export enum ExecutionConfigType {
  Model = "model",
}

export type EntryPointConfigBase = {
  type: EntryPointType;
  params: string[];
};

export type ExecutionConfigBase = {
  type: ExecutionConfigType;
};

export type AppLoadEntryPointConfig = EntryPointConfigBase;

export type DataEntryPointConfig = EntryPointConfigBase & {
  model: ModelName;
};

export type DataScopeEntryPointConfig = EntryPointConfigBase & {
  model: ModelName;
  dataScope: "selection" | "focus";
};

export type ScheduleEntryPointConfig = {
  cron: string;
} & EntryPointConfigBase;

export type ButtonEntryPointConfig = {
  userInputSchema: ModelDataTypes | null;
} & EntryPointConfigBase;

export type EventEntryPointConfig = {
  eventType: EventEntryPoint;
  userInput?: Record<string, unknown>;
} & EntryPointConfigBase;

export type ExitPointConfigBase = {
  type: ExitPointType;
};

export type ExitFieldDefinition = {
  field: string;
  data_type: SchemaColumnType;
  data_type_details: ColumnDataType;

  // CamelCase Keys keep ts compiler happy.
  dataType?: SchemaColumnType;
  dataTypeDetails?: ColumnDataType;
};

// New DataExitPointConfig
export type DataExitPointConfigV2 = {
  model: ModelName;
  fields: ExitFieldDefinition[];
  users: PrimaryKey[];
} & ExitPointConfigBase;

export type DataExitPointConfigLegacy = {
  field: string;
  data_type: SchemaColumnType;
  data_type_details: ColumnDataType;
  model: ModelName;
  users: PrimaryKey[];
} & ExitPointConfigBase;

export type DataExitPointConfig = DataExitPointConfigLegacy | DataExitPointConfigV2;

export type DataFieldExitFieldDefinition = ExitFieldDefinition & {
  create_new_field: boolean;

  // CamelCase Keys keep ts compiler happy.
  createNewField?: boolean;
};

export type DataFieldExitPointConfigV2 = {
  model: ModelName;
  fields: DataFieldExitFieldDefinition[];
  users: PrimaryKey[];
} & ExitPointConfigBase;

export type DataFieldExitPointConfigLegacy = {
  field: string;
  data_type: SchemaColumnType;
  data_type_details: ColumnDataType;
  create_new_field: boolean;
  users: PrimaryKey[];
  model: ModelName;
} & ExitPointConfigBase;

export type DataFieldExitPointConfig = DataFieldExitPointConfigLegacy | DataFieldExitPointConfigV2;

export type DataRowExitPointConfig = {
  shouldDraft: boolean;
  shouldFocus: boolean;
  model: ModelName;
} & ExitPointConfigBase;

export type DataTableExitPointConfig = {
  exit_name: string;
  draftTable: boolean;
} & ExitPointConfigBase;

export type NotifyExitPointConfig = {
  email: string;
  content: string;
} & ExitPointConfigBase;

export type FittingsActionConfig = {
  fittings: any; // TODO: Type me
} & ExitPointConfigBase;

export type EntryPointConfig =
  | AppLoadEntryPointConfig
  | ScheduleEntryPointConfig
  | ButtonEntryPointConfig
  | EventEntryPointConfig
  | DataEntryPointConfig
  | DataScopeEntryPointConfig;

export type ModelConfigItem = {
  model: ModelName;
  scoped_data: ScaleDataState;
  variableName: string;
};

export type ExecutionConfig = {
  data_sources: ModelConfigItem[];
};

export type ExitPointConfig =
  | DataExitPointConfig
  | NotifyExitPointConfig
  | FittingsActionConfig
  | DataRowExitPointConfig
  | DataFieldExitPointConfig
  | DataTableExitPointConfig;

export type Results = { inputs: unknown[]; results: unknown[] };

export type CodeExecutionConfigModel = TemplateBaseModel & {
  // entry
  entry_point_config: EntryPointConfig;
  // execution
  user_function: PrimaryKey;
  execution_config: ExecutionConfig;
  // exit
  exit_point_config: ExitPointConfig;
};

export type CodeConfigurationsCardProps = {
  onEdit?: SetState<PrimaryKey | null>;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /** Active search term — used to highlight matching substrings in the card label. */
  searchTerm?: string;
} & CodeExecutionConfigModel;
